import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Select, Button, Spin, Typography, Space, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { message } from '../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconCalendarOff, IconCircleCheck, IconReceipt2, IconRoute, IconFileInvoice, IconMessage, IconArrowBackUp } from '@tabler/icons-react';
import dayjs, { Dayjs } from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import { actionButtonProps } from '../../constants/actionButtonProps';
import BookingActionsRow from '../../components/BookingActionsRow';
import RespondToReviewModal from '../../components/RespondToReviewModal';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import HostCancelBookingModal from '../../components/HostCancelBookingModal';
import PartialRefundSheet from '../../components/PartialRefundSheet';
import HostDepositShortfallSheet from '../../components/HostDepositShortfallSheet';
import ProposeNewQuoteAmountModal from '../../components/ProposeNewQuoteAmountModal';
import RejectQuoteModal from '../../components/RejectQuoteModal';
import QuoteBreakdownSheet from '../../components/QuoteBreakdownSheet';
import BookingCard from '../../components/BookingCard';
import BookingStatusFilter from '../../components/BookingStatusFilter';
import { getIncomingBookings, markBookingNoShow, acceptQuoteBooking, markServiceCompleted } from '../../services/BookingService';
import { canMarkServiceCompleted, isServiceOnlyBooking } from '../../helpers/serviceBookingHelper';
import { getInvoicesForBooking, downloadInvoice } from '../../services/InvoiceService';
import { onReceiveNotification } from '../../services/RealtimeService';
import { useIncomingBookingsBadge } from '../../contextProviders/IncomingBookingsProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { IBooking, BookingStatus } from '../../interfaces/heavyDutyMachinery/IBooking';
import { BOOKING_CHANGED_NOTIFICATION_TYPES } from '../../interfaces/heavyDutyMachinery/INotification';
import { TIMELINE_VISIBLE_STATUSES, CHAT_ENABLED_STATUSES } from '../../constants/bookingStatusOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { canAcceptQuote } from '../../helpers/quoteHelper';
import { formatFullDate } from '../../helpers/dateFormatHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { CORAL, CORAL_SOFT_BG, CORAL_RING, CORAL_RING_CLEAR } from '../../theme/creamyTokens';

type HostBookingsView = 'list' | 'calendar';

// Mobile-only pill tabs, same look/pattern as MyBookings' own
// MobilePillTabs (Próximas/Pasadas) -- kept as its own local copy rather
// than shared, same reasoning that file's version already documents: each
// page's instance is small and otherwise unrelated.
const HostViewTabs: React.FC<{ value: HostBookingsView; onChange: (value: HostBookingsView) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const options: { value: HostBookingsView; labelKey: string }[] = [
    { value: 'list', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_TAB_LIST },
    { value: 'calendar', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_TAB_CALENDAR },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? CORAL_SOFT_BG : 'transparent',
              color: active ? CORAL : token.colorTextSecondary,
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
          >
            {translate(t, option.labelKey)}
          </button>
        );
      })}
    </div>
  );
};

// A booking "covers" a calendar day if its [startAt, endAt) window overlaps
// that day's [00:00, 24:00) window at all -- one mode-agnostic test that
// correctly handles a same-day Hourly slot, an Overnight stay spanning
// several days (the day endAt falls on excluded, since endAt is that day's
// midnight and nobody rents it -- see rentalPeriodHelper), and a single-day
// DayPass alike.
const dayOverlapsBooking = (booking: IBooking, date: Dayjs) =>
  dayjs(booking.startAt).isBefore(date.endOf('day')) && dayjs(booking.endAt).isAfter(date.startOf('day'));

// AwaitingQuote/AwaitingPayment: totalPrice itself only catches up to
// quoteCurrentAmount once accept-quote actually runs (see BookingController.
// AcceptQuote) -- showing quoteCurrentAmount here (falling back to
// totalPrice for every other status, where it's undefined) is what lets the
// host see the CURRENT proposed number, not a stale one from before a
// revision.
const displayPrice = (booking: IBooking) => (booking.quoteCurrentAmount ?? booking.totalPrice).toFixed(2);

// Scoped per host so a shared/kiosk browser can't leak one host's "already
// seen" state into another's session.
const lastSeenStorageKey = (userId: string) => `heavyDutyMachinery_incoming_bookings_last_seen_${userId}`;

const { Text } = Typography;

// Two slow coral breaths around the row a notification pointed at, then
// nothing. Long enough to find with your eyes after the scroll settles,
// short enough that it never becomes chrome you have to dismiss -- and it
// leaves no permanent mark, because "this is the one you came for" stops
// being true the moment you've seen it.
//
// Animating the ring rather than the row's own background keeps it off
// every other visual state the card already encodes (new, cancelled, past).
const HIGHLIGHT_PULSE = {
  boxShadow: [
    `0 0 0 0px ${CORAL_RING_CLEAR}`,
    `0 0 0 7px ${CORAL_RING}`,
    `0 0 0 0px ${CORAL_RING_CLEAR}`,
  ],
};
const HIGHLIGHT_TRANSITION = { duration: 1.3, times: [0, 0.4, 1], repeat: 1, ease: 'easeInOut' as const };

// Only a newly-arrived booking (a key AnimatePresence hasn't seen before)
// actually plays this -- see the `initial={false}` on both AnimatePresence
// wrappers below, which skips it for the list that's already there on
// mount. Same "fade up into place" language as PayoutIssueBanner/Toast's
// own entry animations.
const NEW_BOOKING_ENTRY_ANIMATION = {
  initial: { opacity: 0, scale: 0.96, y: -6 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0 },
  transition: { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] as const },
};

interface IncomingBookingsProps {
  // When rendered inline inside MyBookings' mobile host toggle (see that
  // page's own doc comment), the outer AppLayout is already provided by the
  // caller -- rendering a second one here would nest Header/Footer inside
  // themselves instead of just showing the list.
  embedded?: boolean;
}

const IncomingBookings: React.FC<IncomingBookingsProps> = ({ embedded }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | undefined>(undefined);
  const [respondReviewId, setRespondReviewId] = useState<string | undefined>(undefined);
  const [noShowSheet, setNoShowSheet] = useState<{ booking: IBooking; party: 'Host' | 'Guest' } | undefined>(undefined);
  const [cancelBookingId, setCancelBookingId] = useState<string | undefined>(undefined);
  // The booking whose partial refund is being written, if any -- the whole
  // booking rather than its id, because the sheet needs its currency and
  // its own refundable ceiling.
  const [refundBooking, setRefundBooking] = useState<IBooking | undefined>(undefined);
  // The amount a host just failed to accept for want of deposit -- undefined
  // whenever that sheet should be closed.
  const [depositShortfallAmount, setDepositShortfallAmount] = useState<number | undefined>(undefined);
  const [proposeAmountBooking, setProposeAmountBooking] = useState<IBooking | undefined>(undefined);
  const [rejectQuoteBookingId, setRejectQuoteBookingId] = useState<string | undefined>(undefined);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | undefined>(undefined);
  // The booking whose breakdown is open, if any -- see buildActions'
  // AwaitingQuote branch for why this is the primary action there.
  const [quoteBooking, setQuoteBooking] = useState<IBooking | undefined>(undefined);
  const [completeServiceBooking, setCompleteServiceBooking] = useState<IBooking | undefined>(undefined);
  const [completingServiceId, setCompletingServiceId] = useState<string | undefined>(undefined);
  // The booking a notification pointed at (see the backend's own
  // "?bookingId=" on every incoming-bookings link). Cleared once the pulse
  // has played, so a re-render or a filter change doesn't replay it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedId, setHighlightedId] = useState<string | undefined>(undefined);
  const bookingRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | undefined>(undefined);
  const { setViewingIncomingBookings } = useIncomingBookingsBadge();

  // The host is only ever the recipient of the PlatformCommission invoice
  // (see InvoicesController's own doc comment) -- always 0 or 1, no picker
  // needed, just download whatever comes back.
  const handleDownloadInvoice = async (bookingId: string) => {
    setDownloadingInvoiceId(bookingId);
    try {
      const invoices = await getInvoicesForBooking(bookingId);
      if (invoices.length === 0) {
        message.info(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_NOT_AVAILABLE));
        return;
      }
      await downloadInvoice(invoices[0]);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_ERROR));
    } finally {
      setDownloadingInvoiceId(undefined);
    }
  };

  // Mobile-only "Entrantes"/"Calendario" tabs (see HostViewTabs) -- desktop
  // keeps the plain full list it already had, untouched.
  const [hostView, setHostView] = useState<HostBookingsView>('list');
  const [calendarMonth, setCalendarMonth] = useState<Dayjs>(dayjs());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Dayjs | undefined>(undefined);
  // Mobile-only status filter (see BookingStatusFilter) -- feeds both
  // "Entrantes" and Calendario (its day markers and day-detail list alike),
  // since every one of them renders from this same filtered array. Empty
  // means no filter, every status shows.
  const [statusFilter, setStatusFilter] = useState<BookingStatus[]>([]);

  // Tells IncomingBookingsProvider "the host is actually looking at this
  // list right now" -- true for as long as this component is mounted,
  // whether that's this page's own /heavy-duty-machinery/bookings/incoming route
  // (desktop) or embedded inside MyBookings' mobile guest/host toggle,
  // which is what makes this (mount-based) work there where a route check
  // couldn't -- see that provider's own doc comment for why.
  useEffect(() => {
    setViewingIncomingBookings(true);
    return () => setViewingIncomingBookings(false);
  }, [setViewingIncomingBookings]);

  // Which bookings get the "Nueva" pill (see BookingCard) -- everything
  // created after the host's PREVIOUS visit to this list, same "unread
  // since last time" idea an inbox uses rather than a fixed time window
  // that would keep reshuffling cards mid-session. Captured once at mount
  // (before overwriting the stored timestamp) so it stays stable for this
  // entire viewing session, including any booking that arrives live while
  // it's open -- undefined on a host's very first-ever visit, when nothing
  // should be flagged since there's no prior visit to compare against.
  const lastSeenAtRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!userId) return;
    const key = lastSeenStorageKey(userId);
    lastSeenAtRef.current = localStorage.getItem(key) ?? undefined;
    localStorage.setItem(key, new Date().toISOString());
  }, [userId]);

  const newBookingIds = useMemo(() => {
    const lastSeenAt = lastSeenAtRef.current;
    if (!lastSeenAt) return new Set<string>();
    return new Set(bookings.filter((b) => dayjs(b.createdAt).isAfter(lastSeenAt)).map((b) => b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  // Feeds "Entrantes" and Calendario alike (see statusFilter's own comment
  // above) -- computed once here rather than re-filtering bookings in each
  // of the several places below that used to read it directly.
  const filteredBookings = useMemo(
    () => (statusFilter.length > 0 ? bookings.filter((b) => statusFilter.includes(b.status)) : bookings),
    [bookings, statusFilter]
  );

  // The backend only returns these ordered by StartAt descending (see
  // BookingController.GetIncomingBookings), which mixes future and past
  // bookings together by raw date -- a booking three months out would sit
  // above one starting tomorrow. Same two-tier ordering MyBookings' own
  // Próximas/Pasadas tabs already use, just without a tab switcher here:
  // what's coming up soonest leads (ascending), everything already past
  // follows underneath, most recently finished first (descending) -- so
  // the list still reads top-to-bottom as "what do I need to look at next",
  // then "what just happened".
  const sortedBookings = useMemo(() => {
    const now = dayjs();
    const upcoming = filteredBookings
      .filter((b) => dayjs(b.startAt).isAfter(now))
      .sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)));
    const past = filteredBookings
      .filter((b) => !dayjs(b.startAt).isAfter(now))
      .sort((a, b) => dayjs(b.startAt).diff(dayjs(a.startAt)));
    return [...upcoming, ...past];
  }, [filteredBookings]);

  useEffect(() => {
    const loadBookings = () =>
      getIncomingBookings()
        .then(setBookings)
        .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_LOAD_ERROR)))
        .finally(() => setLoading(false));

    loadBookings();

    // Anything that changes a booking shows up on its own (AnimatePresence
    // below fades the card in, and the quote card re-renders into whatever
    // state it is now in) instead of needing a manual refresh -- see
    // IncomingBookingsProvider for the matching nav-badge count these same
    // pushes feed when the host is elsewhere.
    //
    // The set is shared (BOOKING_CHANGED_NOTIFICATION_TYPES) rather than
    // listed here: this used to match 'NewBooking' alone, which a reserva
    // diferida never sends -- it arrives as 'NewQuoteRequest' -- so a quote
    // request left this list stale, and clicking its notification landed on
    // a ?bookingId= that the list had never loaded and so went nowhere.
    return onReceiveNotification((notification) => {
      if (BOOKING_CHANGED_NOTIFICATION_TYPES.includes(notification.type)) loadBookings();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Land on the row the notification was about, not just on the list. Runs
  // once the bookings are actually rendered (refs only exist then), scrolls
  // it into the middle of the viewport and plays the pulse.
  //
  // The param is dropped from the URL immediately afterwards: it describes
  // one arrival, not a state of the page, so it must not survive a reload or
  // a back-navigation and replay itself.
  useEffect(() => {
    const target = searchParams.get('bookingId');
    if (!target || loading) return;
    if (!bookings.some((b) => b.id === target)) return;

    setHighlightedId(target);
    // rAF so the scroll happens after this render's layout, otherwise the
    // node may still be at its pre-animation position.
    const frame = requestAnimationFrame(() => {
      bookingRefs.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    const next = new URLSearchParams(searchParams);
    next.delete('bookingId');
    setSearchParams(next, { replace: true });

    // Matches HIGHLIGHT_TRANSITION's own total run (1.3s x 2 passes).
    const timeout = setTimeout(() => setHighlightedId(undefined), 2800);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, bookings, searchParams]);

  /**
   * "El trabajo está hecho."
   *
   * The server captures the held money BEFORE it stamps the milestone, so a
   * card that refuses leaves the booking still open rather than closed and
   * unpaid -- which is why an error here is worth showing verbatim: it is
   * usually the bank's own words about why the charge did not go through, and
   * the professional is standing in somebody's hallway reading it.
   */
  const handleMarkServiceCompleted = async (id: string) => {
    setCompletingServiceId(id);
    try {
      const updated = await markServiceCompleted(id);
      setBookings((prev) => prev.map((booking) => (booking.id === id ? updated : booking)));
      message.success(translate(t, TRANSLATIONS.SERVICE_COMPLETED_SUCCESS));
      setCompleteServiceBooking(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_COMPLETED_ERROR));
    } finally {
      setCompletingServiceId(undefined);
    }
  };

  const handleAcceptQuote = async (id: string) => {
    setAcceptingQuoteId(id);
    try {
      const updated = await acceptQuoteBooking(id);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_SUCCESS));
    } catch (error: any) {
      // The host's deposit will not stretch to this one. Not a toast: this is
      // the moment they are most willing to act, with a customer waiting and
      // a price already agreed, so it gets a sheet that names the figure and
      // puts them one tap from paying it. Same sentinel-in-the-message shape
      // the rate limiter already uses (see ApiService.parseErrorMessage).
      if (error.message === 'HostDepositInsufficient') {
        const booking = bookings.find((b) => b.id === id);
        setDepositShortfallAmount(booking?.quoteCurrentAmount ?? booking?.totalPrice ?? 0);
      } else {
        message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_ERROR));
      }
    } finally {
      setAcceptingQuoteId(undefined);
    }
  };

  const handleNoShow = async (id: string, party: 'Host' | 'Guest') => {
    setUpdatingId(id);
    try {
      const updated = await markBookingNoShow(id, party);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_NO_SHOW_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_NO_SHOW_ERROR));
    } finally {
      setUpdatingId(undefined);
    }
  };

  // See MyBookings' identical `buildActions(booking)` doc comment -- the
  // same buttons feed the mobile card and the desktop list.
  // Two groups, not one row: see BookingActionsRow for why cancelling and
  // refunding are pulled out of the run of things that only look something up.
  const buildActions = (booking: IBooking): { neutral: React.ReactNode[]; risky: React.ReactNode[] } => {
    const actions: React.ReactNode[] = [];
    const risky: React.ReactNode[] = [];
    // Same first action the guest gets in MyBookings, so both sides reach the
    // same shared picture of the rental from the same place. AwaitingQuote is
    // the one TIMELINE_VISIBLE_STATUSES entry left out here: its own branch
    // below replaces the action set entirely with the three quote decisions,
    // which is all a host should be doing at that point.
    if (booking.status !== 'AwaitingQuote' && TIMELINE_VISIBLE_STATUSES.includes(booking.status)) {
      actions.push(
        <Button key="timeline" size="small" icon={<IconRoute size={14} />} {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}`)}>
          {translate(t, TRANSLATIONS.BOOKING_TIMELINE_VIEW_BUTTON)}
        </Button>
      );
    }
    // The thread is open from the moment a quote exists until the rental is
    // over -- exactly the statuses the backend lets a message through on
    // (ConversationsController.SendMessage, and CHAT_ENABLED_STATUSES which
    // mirrors it). It used to be offered only on Confirmed/Completed, which
    // left a booking waiting on payment as the one state with a live thread
    // and no way in. AwaitingQuote is excluded here because its own branch
    // below places the same button among the quote decisions, where it
    // belongs.
    if (booking.status !== 'AwaitingQuote' && CHAT_ENABLED_STATUSES.includes(booking.status)) {
      actions.push(
        <Button key="chat" size="small" icon={<IconMessage size={14} />} {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_BUTTON)}
        </Button>
      );
    }
    // None of the three below is a thing that happens on a service: there is no
    // machine to hand over, so no albarán and no check-in report, and a service
    // booking receives no invoice by any path today (StripeController skips the
    // whole confirmation helper when there is no machine). Offering them was
    // three buttons that either did nothing or opened a form about a machine
    // that does not exist.
    if (!isServiceOnlyBooking(booking) && (booking.status === 'Confirmed' || booking.status === 'Completed')) {
      actions.push(
        <Button
          key="invoice"
          size="small"
          icon={<IconReceipt2 size={14} />}
          loading={downloadingInvoiceId === booking.id}
          {...actionButtonProps}
          onClick={() => handleDownloadInvoice(booking.id)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_BUTTON)}
        </Button>
      );
      // Fase 6: host-only, always offered for either report type -- the
      // flow page itself shows a read-only summary instead of the capture
      // steps once one already exists for this booking (see CheckInOutFlow).
      actions.push(
        <Button key="check-in" size="small" {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}/check-in-out/CheckIn`)}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_CHECK_IN_BUTTON)}
        </Button>
      );
      actions.push(
        <Button key="check-out" size="small" {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}/check-in-out/CheckOut`)}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_CHECK_OUT_BUTTON)}
        </Button>
      );
    }
    // The service side's albarán, and the most consequential button on this
    // page: it charges the held money and starts the customer's 24 hours. Which
    // is why it goes through a confirmation that says so in those words rather
    // than firing on the tap -- and why it is primary, because on a finished job
    // it is the only thing left to do.
    if (canMarkServiceCompleted(booking)) {
      actions.push(
        <Button
          key="service-completed"
          size="small"
          type="primary"
          icon={<IconCircleCheck size={14} />}
          {...actionButtonProps}
          onClick={() => setCompleteServiceBooking(booking)}
        >
          {translate(t, TRANSLATIONS.SERVICE_COMPLETED_BUTTON)}
        </Button>
      );
    }

    // Reserva diferida -- Presupuestos pendientes. Replaces the normal
    // action set for this status entirely (an AwaitingQuote booking has no
    // chat-confirmed check-in/checkout/cancel actions yet -- see
    // BookingController.AcceptQuote/ProposeQuoteAmount/RejectQuote's own
    // doc comments).
    //
    // "Ver presupuesto" comes FIRST and is the primary action, because the
    // three decisions below are unanswerable without it: this row shows one
    // total, and nobody can accept, counter or reject a price they can't
    // break down. The sheet it opens carries the same three decisions at
    // its foot, so a host who opens it never has to come back out here to
    // act on what they just read.
    if (booking.status === 'AwaitingQuote') {
      actions.push(
        <Button
          key="view-quote"
          type="primary"
          size="small"
          icon={<IconFileInvoice size={14} />}
          {...actionButtonProps}
          onClick={() => setQuoteBooking(booking)}
        >
          {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_VIEW_BUTTON)}
        </Button>,
        // Hidden once the live offer is the host's OWN counter-proposal:
        // the ball is then in the guest's court and accepting here would be
        // the host approving their own revised price, skipping the guest
        // entirely. See quoteHelper -- the guest side enforces the mirror
        // image of this same rule.
        ...(canAcceptQuote(booking, userId ?? undefined)
          ? [
              <Button
                key="accept-quote"
                size="small"
                {...actionButtonProps}
                loading={acceptingQuoteId === booking.id}
                onClick={() => handleAcceptQuote(booking.id)}
              >
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_BUTTON)}
              </Button>,
            ]
          : []),
        <Button key="propose-quote" size="small" {...actionButtonProps} onClick={() => setProposeAmountBooking(booking)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_BUTTON)}
        </Button>,
        // The negotiation actually happens in the thread -- the breakdown is
        // there too, plus the one thing this page can't offer: asking the
        // customer what they actually need before pricing it.
        <Button
          key="chat"
          size="small"
          icon={<IconMessage size={14} />}
          {...actionButtonProps}
          onClick={() => navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_BUTTON)}
        </Button>
      );
      risky.push(
        <Button key="reject-quote" danger size="small" {...actionButtonProps} onClick={() => setRejectQuoteBookingId(booking.id)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_REJECT_BUTTON)}
        </Button>
      );
      return { neutral: actions, risky };
    }
    // Unlike the guest's own self-service cancel (BookingController.CancelBooking),
    // this has no CancellationPolicy window -- see HostCancelBooking's own
    // comment for why that window doesn't apply to the host's decision.
    if (booking.status === 'Pending' || booking.status === 'Confirmed') {
      risky.push(
        <Button key="host-cancel" danger size="small" {...actionButtonProps} onClick={() => setCancelBookingId(booking.id)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_BUTTON)}
        </Button>
      );
    }
    // Giving part of it back, for everything short of cancelling. Offered
    // strictly off refundableRemaining -- the server's own figure, not a
    // subtraction done here -- so the button is absent exactly when there is
    // nothing left to give, including on a booking that was never charged.
    // Still offered on a Completed booking: most of the reasons to refund
    // (a day lost, damage settled, hours that were not worked) are only
    // known once the rental is over.
    if ((booking.refundableRemaining ?? 0) > 0 && booking.status !== 'Cancelled') {
      risky.push(
        // danger, like "Cancelar reserva" beside it: this moves real money
        // out of a real account and there is no undo. IconArrowBackUp is the
        // project's own refund mark -- BookingMoneyCard already draws it for
        // the "reembolsado" state of this very booking. It used to be
        // IconCoinOff, a coin with a line through it, which is the icon for
        // something being unavailable, not for giving it back.
        <Button key="refund" danger size="small" icon={<IconArrowBackUp size={14} />} {...actionButtonProps} onClick={() => setRefundBooking(booking)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REFUND_ACTION)}
        </Button>
      );
    }
    // Nobody can know whether either side showed up until the booked window
    // has actually run its course -- same rule BookingController.MarkNoShow
    // now enforces server-side; hiding it here too, rather than just letting
    // the button click fail into an error toast, is what actually stops a
    // host from being offered this action on a days-away future booking.
    if (booking.status === 'Confirmed' && dayjs(booking.endAt).isBefore(dayjs())) {
      actions.push(
        <Button
          key="guest-no-show"
          size="small"
          {...actionButtonProps}
          loading={updatingId === booking.id}
          onClick={() => setNoShowSheet({ booking, party: 'Guest' })}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_GUEST_NO_SHOW_BUTTON)}
        </Button>,
        <Button
          key="host-no-show"
          size="small"
          {...actionButtonProps}
          loading={updatingId === booking.id}
          onClick={() => setNoShowSheet({ booking, party: 'Host' })}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_HOST_NO_SHOW_BUTTON)}
        </Button>
      );
    }
    if (booking.reviewId && !booking.reviewHasHostResponse) {
      actions.push(
        <Button key="respond-review" size="small" {...actionButtonProps} type="primary" onClick={() => setRespondReviewId(booking.reviewId)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEW_RESPOND_BUTTON)}
        </Button>
      );
    }
    return { neutral: actions, risky };
  };

  // Bounds the year dropdown to a sensible browsing window -- nothing in
  // the data actually constrains it (unlike AvailabilityCalendar's real
  // max-advance-booking-days limit), so this is just "far enough back to
  // review last year, far enough ahead to plan next year".
  const calendarMinYear = dayjs().year() - 1;
  const calendarMaxYear = dayjs().year() + 2;

  // One green marker language across the month grid: a whole day filled in
  // (Overnight/DayPass bookings occupy the entire day) vs. a small dot for
  // a same-day Hourly booking that only takes up part of it -- so a fully-
  // booked night reads as more "taken" than a couple of hours would.
  // Cancelled bookings never count -- a day with only a cancelled booking
  // renders exactly like an empty one.
  const calendarCellRender = (date: Dayjs) => {
    const dayBookings = filteredBookings.filter((b) => b.status !== 'Cancelled' && dayOverlapsBooking(b, date));
    const hasFullDayBooking = dayBookings.some((b) => b.rentalMode !== 'Hourly');
    const hasHourlyBooking = dayBookings.some((b) => b.rentalMode === 'Hourly');
    const isSelected = !!selectedCalendarDate && date.isSame(selectedCalendarDate, 'day');

    return (
      <div
        style={{
          position: 'relative',
          textAlign: 'center',
          borderRadius: 14,
          margin: '0 2px',
          padding: '4px 0',
          background: hasFullDayBooking ? token.colorSuccessBg : undefined,
          color: hasFullDayBooking ? token.colorSuccess : undefined,
          fontWeight: hasFullDayBooking ? 700 : undefined,
          border: isSelected ? `1.5px solid ${token.colorSuccess}` : '1.5px solid transparent',
          boxSizing: 'border-box',
        }}
      >
        {date.date()}
        {hasHourlyBooking && !hasFullDayBooking && (
          <span
            style={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: token.colorSuccess,
            }}
          />
        )}
      </div>
    );
  };

  const renderCalendarView = () => {
    // Ascending, not the list tab's upcoming-first/past-first split above --
    // a single day's own bookings read naturally as a small daily agenda,
    // earliest slot at top.
    const selectedDayBookings = selectedCalendarDate
      ? filteredBookings.filter((b) => dayOverlapsBooking(b, selectedCalendarDate)).sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)))
      : [];

    return (
      <div>
        <Calendar
          fullscreen={false}
          value={calendarMonth}
          onPanelChange={(value) => setCalendarMonth(value)}
          onSelect={(date, info) => {
            if (info.source !== 'date') return;
            setSelectedCalendarDate(date);
          }}
          fullCellRender={(date) => calendarCellRender(date)}
          // Same reasoning as AvailabilityCalendar's own headerRender:
          // antd's default header includes a Year view, which calls
          // fullCellRender once per MONTH instead of once per day --
          // that renders this calendar's day-level booking markers as
          // nonsense repeated across every month. Only ever offering our
          // own year/month dropdowns keeps the Calendar's body from
          // entering that view at all.
          headerRender={({ value, onChange }) => (
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Select
                size="small"
                value={value.year()}
                style={{ width: 90 }}
                onChange={(y) => onChange(value.clone().year(y))}
                options={Array.from({ length: calendarMaxYear - calendarMinYear + 1 }, (_, i) => calendarMinYear + i).map((y) => ({
                  value: y,
                  label: y,
                }))}
              />
              <Select
                size="small"
                value={value.month()}
                style={{ width: 90 }}
                onChange={(m) => onChange(value.clone().month(m))}
                options={Array.from({ length: 12 }, (_, m) => ({ value: m, label: dayjs().month(m).format('MMM') }))}
              />
            </div>
          )}
        />

        <div style={{ display: 'flex', gap: 16, marginTop: 4, marginBottom: 16 }}>
          <Space size={4}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: token.colorSuccessBg, border: `1px solid ${token.colorSuccess}` }} />
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_CALENDAR_LEGEND_FULL_DAY)}</Text>
          </Space>
          <Space size={4}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: token.colorSuccess }} />
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_CALENDAR_LEGEND_HOURLY)}</Text>
          </Space>
        </div>

        {selectedCalendarDate && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {formatFullDate(selectedCalendarDate, i18n.language)}
            </Text>
            {selectedDayBookings.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_CALENDAR_DAY_EMPTY)}</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedDayBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    title={booking.heavyDutyMachineryTitle}
                    thumbnailUrl={booking.heavyDutyMachineryThumbnailUrl}
                    reference={booking.reference}
                    status={booking.status}
                    dateRangeLabel={`${dayjs(booking.startAt).format('DD/MM/YYYY HH:mm')} – ${dayjs(booking.endAt).format('DD/MM/YYYY HH:mm')}`}
                    priceLabel={`${displayPrice(booking)}€`}
                    extraLine={`${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_GUEST_LABEL)}: ${booking.guestDisplayName || booking.guestUserId}`}
                    muted={booking.status === 'Cancelled' || dayjs(booking.endAt).isBefore(dayjs())}
                    isNew={newBookingIds.has(booking.id)}
                    actions={<BookingActionsRow {...buildActions(booking)} />}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <>
      <div style={{ width: '100%', maxWidth: 900 }}>
        <h2 style={{ marginBottom: 24 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_TITLE)}</h2>

        {/* The status filter is a row of pills and works at any width -- it
            was isMobile-gated only because it was built during the mobile
            pass. The list/calendar switch below still is: that calendar was
            drawn at phone width and has not had its desktop pass yet. */}
        <BookingStatusFilter value={statusFilter} onChange={setStatusFilter} />
        {isMobile && <HostViewTabs value={hostView} onChange={setHostView} />}

        {isMobile && hostView === 'calendar' ? (
          loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            renderCalendarView()
          )
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : bookings.length === 0 ? (
          <EmptyState
            icon={<IconCalendarOff size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_EMPTY)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_EMPTY_DESCRIPTION)}
          />
        ) : sortedBookings.length === 0 ? (
          // Bookings exist, none match the currently-checked statuses -- a
          // lighter inline message than the full EmptyState illustration,
          // since this isn't "you have nothing", just "not with this filter".
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKINGS_FILTER_NO_MATCHES)}</Text>
          </div>
        ) : isMobile ? (
          // Mobile: pill cards. The coral comes from the app-wide theme now
          // (see theme/appTheme) rather than a local override here.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <AnimatePresence initial={false}>
              {sortedBookings.map((booking) => (
                <motion.div
                  key={booking.id}
                  layout
                  {...NEW_BOOKING_ENTRY_ANIMATION}
                  ref={(node) => {
                    bookingRefs.current[booking.id] = node;
                  }}
                  animate={highlightedId === booking.id ? { ...NEW_BOOKING_ENTRY_ANIMATION.animate, ...HIGHLIGHT_PULSE } : NEW_BOOKING_ENTRY_ANIMATION.animate}
                  transition={highlightedId === booking.id ? HIGHLIGHT_TRANSITION : NEW_BOOKING_ENTRY_ANIMATION.transition}
                  style={{ borderRadius: 18 }}
                >
                  <BookingCard
                    title={booking.heavyDutyMachineryTitle}
                    thumbnailUrl={booking.heavyDutyMachineryThumbnailUrl}
                    reference={booking.reference}
                    status={booking.status}
                    dateRangeLabel={`${dayjs(booking.startAt).format('DD/MM/YYYY HH:mm')} – ${dayjs(booking.endAt).format('DD/MM/YYYY HH:mm')}`}
                    priceLabel={`${displayPrice(booking)}€`}
                    extraLine={`${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_GUEST_LABEL)}: ${booking.guestDisplayName || booking.guestUserId}`}
                    muted={booking.status === 'Cancelled' || dayjs(booking.endAt).isBefore(dayjs())}
                    isNew={newBookingIds.has(booking.id)}
                    actions={<BookingActionsRow {...buildActions(booking)} />}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          // The same card the phone gets. This used to be an antd List of
          // divider-separated rows carrying exactly the same six fields --
          // title, reference, status, dates, price, customer -- written out a
          // second time by hand, with no container of its own, so telling
          // where one booking ended and the next began was a matter of
          // spotting a hairline.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <AnimatePresence initial={false}>
              {sortedBookings.map((booking) => (
                <motion.div
                  key={booking.id}
                  layout
                  {...NEW_BOOKING_ENTRY_ANIMATION}
                  ref={(node) => {
                    bookingRefs.current[booking.id] = node;
                  }}
                  animate={highlightedId === booking.id ? { ...NEW_BOOKING_ENTRY_ANIMATION.animate, ...HIGHLIGHT_PULSE } : NEW_BOOKING_ENTRY_ANIMATION.animate}
                  transition={highlightedId === booking.id ? HIGHLIGHT_TRANSITION : NEW_BOOKING_ENTRY_ANIMATION.transition}
                  style={{ borderRadius: 18 }}
                >
                  <BookingCard
                    title={booking.heavyDutyMachineryTitle}
                    thumbnailUrl={booking.heavyDutyMachineryThumbnailUrl}
                    reference={booking.reference}
                    status={booking.status}
                    dateRangeLabel={`${dayjs(booking.startAt).format('DD/MM/YYYY HH:mm')} – ${dayjs(booking.endAt).format('DD/MM/YYYY HH:mm')}`}
                    priceLabel={`${displayPrice(booking)}€`}
                    extraLine={`${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_GUEST_LABEL)}: ${booking.guestDisplayName || booking.guestUserId}`}
                    muted={booking.status === 'Cancelled' || dayjs(booking.endAt).isBefore(dayjs())}
                    isNew={newBookingIds.has(booking.id)}
                    actions={<BookingActionsRow {...buildActions(booking)} />}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {respondReviewId && (
        <RespondToReviewModal
          reviewId={respondReviewId}
          open
          onClose={() => setRespondReviewId(undefined)}
          onSubmitted={() =>
            setBookings((prev) =>
              prev.map((b) => (b.reviewId === respondReviewId ? { ...b, reviewHasHostResponse: true } : b))
            )
          }
        />
      )}

      {cancelBookingId && (
        <HostCancelBookingModal
          bookingId={cancelBookingId}
          open
          onClose={() => setCancelBookingId(undefined)}
          onCancelled={(updated) => setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))}
        />
      )}

      {depositShortfallAmount !== undefined && (
        <HostDepositShortfallSheet
          bookingAmount={depositShortfallAmount}
          open
          onClose={() => setDepositShortfallAmount(undefined)}
        />
      )}

      {refundBooking && (
        <PartialRefundSheet
          booking={refundBooking}
          open
          onClose={() => setRefundBooking(undefined)}
          onRefunded={(updated) => setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))}
        />
      )}

      {/* The breakdown behind the number this page shows. Its own three
          decisions are wired to the same handlers as the row's, so acting
          from inside the sheet and acting from the list are the same thing
          -- and accepting from in here closes it, since there's then
          nothing left to decide. */}
      <QuoteBreakdownSheet
        open={!!quoteBooking}
        booking={quoteBooking}
        userId={userId ?? undefined}
        onClose={() => setQuoteBooking(undefined)}
        accepting={acceptingQuoteId === quoteBooking?.id}
        onAccept={
          quoteBooking && canAcceptQuote(quoteBooking, userId ?? undefined)
            ? async () => {
                await handleAcceptQuote(quoteBooking.id);
                setQuoteBooking(undefined);
              }
            : undefined
        }
        onPropose={() => {
          setProposeAmountBooking(quoteBooking);
          setQuoteBooking(undefined);
        }}
        onReject={() => {
          setRejectQuoteBookingId(quoteBooking?.id);
          setQuoteBooking(undefined);
        }}
      />

      {proposeAmountBooking && (
        <ProposeNewQuoteAmountModal
          booking={proposeAmountBooking}
          open
          onClose={() => setProposeAmountBooking(undefined)}
          onProposed={(updated) => setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))}
        />
      )}

      {rejectQuoteBookingId && (
        <RejectQuoteModal
          bookingId={rejectQuoteBookingId}
          open
          onClose={() => setRejectQuoteBookingId(undefined)}
          onRejected={(updated) => setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))}
        />
      )}

      <ConfirmBottomSheet
        open={!!noShowSheet}
        title={translate(
          t,
          noShowSheet?.party === 'Host' ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_HOST_NO_SHOW_CONFIRM : TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_GUEST_NO_SHOW_CONFIRM
        )}
        description={noShowSheet ? `${noShowSheet.booking.heavyDutyMachineryTitle} · ${dayjs(noShowSheet.booking.startAt).format('DD/MM/YYYY HH:mm')}` : undefined}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(
          t,
          noShowSheet?.party === 'Host' ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_HOST_NO_SHOW_SHEET_CONFIRM : TRANSLATIONS.HEAVY_DUTY_MACHINERY_MARK_GUEST_NO_SHOW_SHEET_CONFIRM
        )}
        confirmLoading={!!noShowSheet && updatingId === noShowSheet.booking.id}
        onConfirm={() => {
          if (noShowSheet) handleNoShow(noShowSheet.booking.id, noShowSheet.party);
          setNoShowSheet(undefined);
        }}
        onClose={() => setNoShowSheet(undefined)}
      />

      {/* The description changes with what actually happens next, because on a
          held booking this button also charges the card and on a prepaid one it
          does not -- one sentence covering both would be wrong for one of
          them. The sheet stays open on failure: the handler closes it only once
          the money is in. */}
      <ConfirmBottomSheet
        open={!!completeServiceBooking}
        title={translate(t, TRANSLATIONS.SERVICE_COMPLETED_CONFIRM_TITLE)}
        description={translate(
          t,
          completeServiceBooking?.captureMode === 'OnHandover'
            ? TRANSLATIONS.SERVICE_COMPLETED_CONFIRM_HELD
            : TRANSLATIONS.SERVICE_COMPLETED_CONFIRM_PAID
        )}
        cancelText={translate(t, TRANSLATIONS.SERVICE_COMPLETED_KEEP_OPEN)}
        confirmText={translate(t, TRANSLATIONS.SERVICE_COMPLETED_CONFIRM_BUTTON)}
        confirmLoading={!!completeServiceBooking && completingServiceId === completeServiceBooking.id}
        onConfirm={() => {
          if (completeServiceBooking) handleMarkServiceCompleted(completeServiceBooking.id);
        }}
        onClose={() => setCompleteServiceBooking(undefined)}
      />
    </>
  );

  return embedded ? content : <AppLayout>{content}</AppLayout>;
};

export default IncomingBookings;
